import type { OrganizationWorkflowCreatedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowCreated';
import type { OrganizationWorkflowDeactivatedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowDeactivated';
import type { OrganizationWorkflowOptionsSavedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowOptionsSaved';
import type { OrganizationWorkflowRestoredAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowRestored';
import type { OrganizationWorkflowSavedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowSaved';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { AccountWorkflowsFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/accountWorkflowsFetched';
import type { BeneficiaryTypeGrantsFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/beneficiaryTypeGrantsFetched';
import type { BeneficiaryTypeGrantsSavedAction } from '@Application/SubjectTypeDefinitions/Actions/beneficiaryTypeGrantsSaved';
import type { GrantableUsersFoundAction } from '@Application/SubjectTypeDefinitions/Actions/grantableUsersFound';
import type { GrantedUsersFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/grantedUsersFetched';
import type { InformationModesFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/informationModesFetched';
import type { SubjectAboutsFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectAboutsFetched';
import type { SubjectTypeColorsFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeColorsFetched';
import type { SubjectTypeIconsFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeIconsFetched';
import type { SubjectFieldNaturesFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectFieldNaturesFetched';
import type { SubjectTypeAccessLevelsFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeAccessLevelsFetched';
import type { SubjectTypeArchivedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeArchived';
import type { SubjectTypeRestoredAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeRestored';
import type { SubjectTypeDefinitionFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeDefinitionFetched';
import type { SubjectTypeDefinitionsFetchedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeDefinitionsFetched';
import type { SubjectTypeDefinitionSavedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeDefinitionSaved';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import AccountWorkflowsView from '@UI/Store/SubjectTypeDefinitions/Views/AccountWorkflowsView';
import AccountWorkflowView from '@UI/Store/SubjectTypeDefinitions/Views/AccountWorkflowView';
import BeneficiaryTypeGrantsView from '@UI/Store/SubjectTypeDefinitions/Views/BeneficiaryTypeGrantsView';
import GrantableUserView from '@UI/Store/SubjectTypeDefinitions/Views/GrantableUserView';
import GrantedUsersListView from '@UI/Store/SubjectTypeDefinitions/Views/GrantedUsersListView';
import SubjectTypeDefinitionsListView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectTypeDefinitionsListView';
import SubjectTypeDefinitionView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectTypeDefinitionView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type SubjectTypeDefinitionsStore = {
	definitions: SubjectTypeDefinitionsListView | null;
	definition: SubjectTypeDefinitionView | null;
	workflows: AccountWorkflowsView | null;
	// Names of the users already granted, resolved by id; never `usersStore.users`.
	grantedUsers: GrantedUsersListView | null;
	// Latest search result of the user picker.
	userCandidates: readonly GrantableUserView[];
	fieldNatures: readonly LabeledValueView[] | null;
	abouts: readonly LabeledValueView[] | null;
	icons: readonly LabeledValueView[] | null;
	colors: readonly LabeledValueView[] | null;
	informationModes: readonly LabeledValueView[] | null;
	accessLevels: readonly LabeledValueView[] | null;
	beneficiaryTypeGrants: BeneficiaryTypeGrantsView | null;
};

const initialState: SubjectTypeDefinitionsStore = {
	definitions: null,
	definition: null,
	workflows: null,
	grantedUsers: null,
	userCandidates: [],
	fieldNatures: null,
	abouts: null,
	icons: null,
	colors: null,
	informationModes: null,
	accessLevels: null,
	beneficiaryTypeGrants: null,
};

type Actions =
	| SubjectTypeDefinitionsFetchedAction
	| SubjectTypeDefinitionFetchedAction
	| SubjectTypeDefinitionSavedAction
	| SubjectTypeArchivedAction
	| SubjectTypeRestoredAction
	| AccountWorkflowsFetchedAction
	| GrantedUsersFetchedAction
	| GrantableUsersFoundAction
	| SubjectFieldNaturesFetchedAction
	| SubjectAboutsFetchedAction
	| SubjectTypeIconsFetchedAction
	| SubjectTypeColorsFetchedAction
	| InformationModesFetchedAction
	| SubjectTypeAccessLevelsFetchedAction
	| BeneficiaryTypeGrantsFetchedAction
	| BeneficiaryTypeGrantsSavedAction
	| OrganizationWorkflowCreatedAction
	| OrganizationWorkflowSavedAction
	| OrganizationWorkflowOptionsSavedAction
	| OrganizationWorkflowDeactivatedAction
	| OrganizationWorkflowRestoredAction
	| UserRevokedAction;

const whenDefinitionsFetched = (
	store: SubjectTypeDefinitionsStore,
	action: SubjectTypeDefinitionsFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	definitions: new SubjectTypeDefinitionsListView(
		action.payload.map((definition) => new SubjectTypeDefinitionView(definition)),
		action.accountId,
	),
});

const whenDefinitionFetched = (
	store: SubjectTypeDefinitionsStore,
	action: SubjectTypeDefinitionFetchedAction | SubjectTypeDefinitionSavedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	definition: new SubjectTypeDefinitionView(action.payload),
});

const whenDefinitionSaved = (
	store: SubjectTypeDefinitionsStore,
	action: SubjectTypeDefinitionSavedAction,
): SubjectTypeDefinitionsStore => ({
	...whenDefinitionFetched(store, action),
	// The list is stale after a save: the screen re-fetches it.
	definitions: null,
});

const whenArchivalChanged = (store: SubjectTypeDefinitionsStore): SubjectTypeDefinitionsStore => ({
	...store,
	definitions: null,
});

const whenWorkflowsFetched = (
	store: SubjectTypeDefinitionsStore,
	action: AccountWorkflowsFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	workflows: new AccountWorkflowsView(
		action.payload.map((workflow) => new AccountWorkflowView(workflow)),
		action.accountId,
	),
});

// An organization's workflows changed: the account workflows are read again on the next mount.
const whenOrganizationWorkflowChanged = (
	store: SubjectTypeDefinitionsStore,
): SubjectTypeDefinitionsStore => ({
	...store,
	workflows: null,
});

const whenGrantedUsersFetched = (
	store: SubjectTypeDefinitionsStore,
	action: GrantedUsersFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	grantedUsers: new GrantedUsersListView(action.payload, action.accountId, action.ids),
});

const whenGrantableUsersFound = (
	store: SubjectTypeDefinitionsStore,
	action: GrantableUsersFoundAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	userCandidates: action.payload.map((user) => new GrantableUserView(user)),
});

const whenFieldNaturesFetched = (
	store: SubjectTypeDefinitionsStore,
	action: SubjectFieldNaturesFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	fieldNatures: action.payload.map((entry) => new LabeledValueView(entry)),
});

const whenAboutsFetched = (
	store: SubjectTypeDefinitionsStore,
	action: SubjectAboutsFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	abouts: action.payload.map((entry) => new LabeledValueView(entry)),
});

const whenIconsFetched = (
	store: SubjectTypeDefinitionsStore,
	action: SubjectTypeIconsFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	icons: action.payload.map((entry) => new LabeledValueView(entry)),
});

const whenColorsFetched = (
	store: SubjectTypeDefinitionsStore,
	action: SubjectTypeColorsFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	colors: action.payload.map((entry) => new LabeledValueView(entry)),
});

const whenInformationModesFetched = (
	store: SubjectTypeDefinitionsStore,
	action: InformationModesFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	informationModes: action.payload.map((entry) => new LabeledValueView(entry)),
});

const whenAccessLevelsFetched = (
	store: SubjectTypeDefinitionsStore,
	action: SubjectTypeAccessLevelsFetchedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	accessLevels: action.payload.map((entry) => new LabeledValueView(entry)),
});

const whenBeneficiaryTypeGrantsReceived = (
	store: SubjectTypeDefinitionsStore,
	action: BeneficiaryTypeGrantsFetchedAction | BeneficiaryTypeGrantsSavedAction,
): SubjectTypeDefinitionsStore => ({
	...store,
	beneficiaryTypeGrants: new BeneficiaryTypeGrantsView(action.payload, action.accountId),
});

const whenUserRevoked = (): SubjectTypeDefinitionsStore => initialState;

export default registerStore<'subjectTypeDefinitionsStore', Actions>(
	'subjectTypeDefinitionsStore',
	createSliceReducer({
		SUBJECT_TYPE_DEFINITIONS_FETCHED: whenDefinitionsFetched,
		SUBJECT_TYPE_DEFINITION_FETCHED: whenDefinitionFetched,
		SUBJECT_TYPE_DEFINITION_SAVED: whenDefinitionSaved,
		SUBJECT_TYPE_ARCHIVED: whenArchivalChanged,
		SUBJECT_TYPE_RESTORED: whenArchivalChanged,
		ACCOUNT_WORKFLOWS_FETCHED: whenWorkflowsFetched,
		GRANTED_USERS_FETCHED: whenGrantedUsersFetched,
		GRANTABLE_USERS_FOUND: whenGrantableUsersFound,
		SUBJECT_FIELD_NATURES_FETCHED: whenFieldNaturesFetched,
		SUBJECT_ABOUTS_FETCHED: whenAboutsFetched,
		SUBJECT_TYPE_ICONS_FETCHED: whenIconsFetched,
		SUBJECT_TYPE_COLORS_FETCHED: whenColorsFetched,
		INFORMATION_MODES_FETCHED: whenInformationModesFetched,
		SUBJECT_TYPE_ACCESS_LEVELS_FETCHED: whenAccessLevelsFetched,
		BENEFICIARY_TYPE_GRANTS_FETCHED: whenBeneficiaryTypeGrantsReceived,
		BENEFICIARY_TYPE_GRANTS_SAVED: whenBeneficiaryTypeGrantsReceived,
		ORGANIZATION_WORKFLOW_CREATED: whenOrganizationWorkflowChanged,
		ORGANIZATION_WORKFLOW_SAVED: whenOrganizationWorkflowChanged,
		ORGANIZATION_WORKFLOW_OPTIONS_SAVED: whenOrganizationWorkflowChanged,
		ORGANIZATION_WORKFLOW_DEACTIVATED: whenOrganizationWorkflowChanged,
		ORGANIZATION_WORKFLOW_RESTORED: whenOrganizationWorkflowChanged,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
